import { supabase } from "@/integrations/supabase/client";
import { User } from "@/types";
import { getCachedUserId, getCachedUser } from "@/services/auth/sessionCache";
import { networkService } from "@/services/network.service";
import { syncUserFeatures } from "@/services/features.service";
import logger from "@/utils/logger";

const LIGHT_THEME_ID = '11111111-1111-1111-1111-111111111111';
const DARK_THEME_ID = '22222222-2222-2222-2222-222222222222';

// Helper per gestione errori coerente
const handleConnectionError = (operation: string, error: any) => {
  const errorDetails = {
    message: error?.message || `Unknown error during ${operation}`,
    details: error?.toString() || '',
    hint: error?.message?.includes("Failed to fetch")
      ? "Check your internet connection or try again later."
      : '',
    code: error?.code || ''
  };

  logger.error(`Error ${operation}:`, errorDetails);
  return null;
};

// Recupera profilo completo utente
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const sessionUser = await getCachedUser();
    if (!sessionUser) {
      return handleConnectionError("getting session", null);
    }

    const userId = sessionUser.id;

    if (!networkService.isOnline()) {
      logger.warn("[getCurrentUser] Offline: restituisco user base");
      const { user_metadata, email } = sessionUser;
      const name = user_metadata?.name || email?.split("@")[0] || "User";

      const removeListener = networkService.addListener(status => {
        if (!status.connected) {
          return;
        }

        (async () => {
          try {
            const { data, error } = await supabase.auth.getSession();
            if (error) {
              logger.error("[getCurrentUser] Errore nel recupero della sessione alla riconnessione", error);
            }

            const activeUser = data.session?.user || null;

            if (!activeUser) {
              logger.warn("[getCurrentUser] Nessuna sessione attiva dopo la riconnessione: interrompo il listener");
              removeListener();
              return;
            }

            if (activeUser.id !== userId) {
              logger.warn(
                "[getCurrentUser] Sessione incoerente alla riconnessione: interrompo il listener"
              );
              removeListener();
              return;
            }

            const existing = await getUserProfile(userId);

            if (existing) {
              removeListener();
              return;
            }

            const created = await createUserProfile(
              userId,
              name,
              email || ""
            );

            if (created) {
              removeListener();
              return;
            }

            logger.warn(
              "[getCurrentUser] Creazione del profilo fallita dopo la riconnessione: interrompo il listener per evitare nuovi tentativi"
            );
            removeListener();
          } catch (listenerError) {
            logger.error(
              "[getCurrentUser] Errore durante la gestione della riconnessione: interrompo il listener",
              listenerError
            );
            removeListener();
          }
        })();
      });

      return {
        id: userId,
        name,
        email: email || "",
        isPremium: false,
        isDarkMode: false,
        role: 'user'
      };
    }

    let profile = await getUserProfile(userId);

    if (!profile) {
      const authUser = sessionUser;
      const name =
        authUser.user_metadata?.name || authUser.email?.split("@")[0] || "User";
      const success = await createUserProfile(
        authUser.id,
        name,
        authUser.email || ""
      );

      if (!success) {
        return {
          id: authUser.id,
          name,
          email: authUser.email || "",
          isPremium: false,
          isDarkMode: false,
          role: 'user'
        };
      }

      profile = await getUserProfile(authUser.id);
      if (!profile) {
        return {
          id: authUser.id,
          name,
          email: authUser.email || "",
          isPremium: false,
          isDarkMode: false,
          role: 'user'
        };
      }
    }

    await syncUserFeatures(profile.id, profile.isPremium);

    return profile;
  } catch (error) {
    return handleConnectionError("getting current user", error);
  }
};

export const getUserProfile = async (userId: string): Promise<User | null> => {
  if (!networkService.isOnline()) {
    logger.warn("[getUserProfile] Offline: salto il fetch del profilo");
    return null;
  }

  try {
    const { data: userData, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      return handleConnectionError("fetching user profile", error);
    }

    if (!userData) {
      return null;
    }

    return {
      id: userData.id,
      name: userData.name || "",
      email: userData.email || "",
      isPremium: userData.is_premium || false,
      isDarkMode: userData.theme_id === DARK_THEME_ID,
      role: (userData.role as User['role']) ?? 'user'
    };
  } catch (error) {
    return handleConnectionError("in getUserProfile", error);
  }
};

export const updateUserProfile = async (name: string): Promise<boolean> => {
  if (!networkService.isOnline()) {
    logger.warn("[updateUserProfile] Offline: impossibile aggiornare il profilo");
    return false;
  }

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      handleConnectionError("getting session for profile update", null);
      return false;
    }

    const { error } = await supabase
      .from("profiles")
      .update({ name, updated_at: new Date().toISOString() })
      .eq("id", userId);

    if (error) {
      handleConnectionError("updating profile", error);
      return false;
    }

    return true;
  } catch (error) {
    return handleConnectionError("updating profile", error) || false;
  }
};

export const updateUserTheme = async (themeId: string): Promise<boolean> => {
  if (!networkService.isOnline()) {
    logger.warn("[updateUserTheme] Offline: impossibile aggiornare il tema");
    return false;
  }

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      handleConnectionError("getting session for theme update", null);
      return false;
    }

    const { error } = await supabase
      .from("profiles")
      .update({ theme_id: themeId })
      .eq("id", userId);

    if (error) {
      handleConnectionError("updating theme preference", error);
      return false;
    }

    return true;
  } catch (error) {
    return handleConnectionError("updating theme preference", error) || false;
  }
};

export const getUserThemePreference = async (): Promise<string | null> => {
  if (!networkService.isOnline()) {
    logger.warn("[getUserThemePreference] Offline: ritorno null");
    return null;
  }

  try {
    const userId = await getCachedUserId();
    if (!userId) {
      handleConnectionError("getting session for theme preference", null);
      return null;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("theme_id")
      .eq("id", userId)
      .single();

    if (error) {
      handleConnectionError("fetching theme preference", error);
      return null;
    }

    return data.theme_id;
  } catch (error) {
    return handleConnectionError("fetching theme preference", error);
  }
};

export const createUserProfile = async (
  userId: string,
  name: string,
  email: string
): Promise<boolean> => {
  if (!networkService.isOnline()) {
    logger.warn("[createUserProfile] Offline: impossibile creare profilo");
    return false;
  }

  try {
    const themeId = LIGHT_THEME_ID;
    const { error } = await supabase
      .from("profiles")
      .upsert([
        {
          id: userId,
          name,
          email,
          theme_id: themeId,
          role: 'user'
        }
      ], { onConflict: 'id' });


    if (error) {
      handleConnectionError("creating profile", error);
      return false;
    }

    return true;
  } catch (error) {
    return handleConnectionError("creating profile", error) || false;
  }
};
